<script setup>
import { computed, onMounted, nextTick, ref, reactive, watch } from 'vue';
import { useSidebar } from 'dashboard/composables/useSidebar';
import { useElementHover, useFocusWithin, useRafFn } from '@vueuse/core';
import { useSidebarContext } from './provider';
import { useRoute, useRouter } from 'vue-router';
import Policy from 'dashboard/components/policy.vue';
import SidebarGroupHeader from './SidebarGroupHeader.vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarSubGroup from './SidebarSubGroup.vue';
import SidebarGroupEmptyLeaf from './SidebarGroupEmptyLeaf.vue';

// v6.4.62: Track expanded subgroups in flyout menu
const flyoutExpandedSubgroups = reactive(new Set());

// v6.4.62: Reset expanded subgroups when flyout closes
const resetFlyoutState = () => flyoutExpandedSubgroups.clear();

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  icon: { type: [String, Object, Function], default: null },
  to: { type: Object, default: null },
  activeOn: { type: Array, default: () => [] },
  children: { type: Array, default: undefined },
  // v6.4.68: Items pinned visible when section is collapsed (e.g. ['All', 'Unread'])
  pinnedItems: { type: Array, default: () => [] },
  getterKeys: { type: Object, default: () => ({}) },
});

const {
  expandedItem,
  setExpandedItem,
  resolvePath,
  resolvePermissions,
  resolveFeatureFlag,
  isAllowed,
} = useSidebarContext();

const { isCollapsed } = useSidebar();
const groupRef = ref(null);
const flyoutRef = ref(null);

// v6.4.61: Hover detection - delay reduzido para transição rápida entre itens do menu
const isGroupHovered = useElementHover(groupRef, { delayLeave: 50 });
const isFlyoutHovered = useElementHover(flyoutRef, { delayLeave: 150 });

// Focus detection for keyboard accessibility
const { focused: isGroupFocused } = useFocusWithin(groupRef);
const { focused: isFlyoutFocused } = useFocusWithin(flyoutRef);

const navigableChildren = computed(() => {
  return props.children?.flatMap(child => child.children || child) || [];
});

const route = useRoute();
const router = useRouter();
const isExpanded = computed(() => expandedItem.value === props.name);
const isExpandable = computed(() => props.children);
const hasChildren = computed(
  () => Array.isArray(props.children) && props.children.length > 0
);

const { top: flyoutTop, left: flyoutLeft } = (() => {
  const top = ref(0);
  const left = ref(0);
  const updatePosition = () => {
    if (groupRef.value) {
      const el = groupRef.value?.$el || groupRef.value;
      const rect = el.getBoundingClientRect();
      top.value = rect.top;
      // v6.4.61: Posicionar flyout à direita da sidebar collapsed (56px de largura + 4px gap)
      // Usar a largura real da sidebar para evitar sobreposição
      const sidebar = el.closest('nav') || el.closest('[class*="sidebar"]');
      if (sidebar) {
        const sidebarRect = sidebar.getBoundingClientRect();
        left.value = sidebarRect.right + 4;
      } else {
        left.value = rect.right + 8;
      }
    }
  };
  const { pause, resume } = useRafFn(updatePosition, { immediate: false });

  watch(
    () =>
      isCollapsed.value &&
      hasChildren.value &&
      (isGroupHovered.value ||
        isFlyoutHovered.value ||
        isGroupFocused.value ||
        isFlyoutFocused.value),
    val => {
      if (val) resume();
      else pause();
    }
  );
  return { top, left };
})();

const flyoutStyle = computed(() => {
  return {
    top: `${flyoutTop.value}px`,
    left: `${flyoutLeft.value}px`,
    maxHeight: 'calc(100vh - 40px)',
  };
});

const showFlyout = computed(
  () =>
    isCollapsed.value &&
    hasChildren.value &&
    (isGroupHovered.value ||
      isFlyoutHovered.value ||
      isGroupFocused.value ||
      isFlyoutFocused.value)
);

const accessibleItems = computed(() => {
  if (!hasChildren.value) return [];
  return props.children.filter(child => {
    // If a item has no link, it means it's just a subgroup header
    // So we don't need to check for permissions here, because there's nothing to
    // access here anyway
    return child.to && isAllowed(child.to);
  });
});

const hasAccessibleChildren = computed(() => {
  return accessibleItems.value.length > 0;
});

const isActive = computed(() => {
  if (props.to) {
    if (route.path === resolvePath(props.to)) return true;

    return props.activeOn.includes(route.name);
  }

  return false;
});

// We could use the RouterLink isActive too, but our routes are not always
// nested correctly, so we need to check the active state ourselves
// TODO: Audit the routes and fix the nesting and remove this
const activeChild = computed(() => {
  const pathSame = navigableChildren.value.find(
    child => child.to && route.path === resolvePath(child.to)
  );
  if (pathSame) return pathSame;

  // Rank the activeOn Prop higher than the path match
  // There will be cases where the path name is the same but the params are different
  // So we need to rank them based on the params
  // For example, contacts segment list in the sidebar effectively has the same name
  // But the params are different
  const activeOnPages = navigableChildren.value.filter(child =>
    child.activeOn?.includes(route.name)
  );

  if (activeOnPages.length > 0) {
    const rankedPage = activeOnPages.find(child => {
      return Object.keys(child.to.params)
        .map(key => {
          return String(child.to.params[key]) === String(route.params[key]);
        })
        .every(match => match);
    });

    // If there is no ranked page, return the first activeOn page anyway
    // Since this takes higher precedence over the path match
    // This is not perfect, ideally we should rank each route based on all the techniques
    // and then return the highest ranked one
    // But this is good enough for now
    return rankedPage ?? activeOnPages[0];
  }

  return navigableChildren.value.find(
    child => child.to && route.path.startsWith(resolvePath(child.to))
  );
});

const hasActiveChild = computed(() => {
  return activeChild.value !== undefined;
});

const toggleTrigger = () => {
  if (
    hasAccessibleChildren.value &&
    !isExpanded.value &&
    !hasActiveChild.value
  ) {
    // if not already expanded, navigate to the first child
    const firstItem = accessibleItems.value[0];
    router.push(firstItem.to);
  }
  setExpandedItem(props.name);
};

onMounted(async () => {
  await nextTick();
  if (hasActiveChild.value) {
    setExpandedItem(props.name);
  }
});

watch(
  hasActiveChild,
  hasNewActiveChild => {
    if (hasNewActiveChild && !isExpanded.value) {
      setExpandedItem(props.name);
    }
  },
  { once: true }
);

// v6.4.62: Reset flyout subgroups when flyout closes
watch(showFlyout, isVisible => {
  if (!isVisible) resetFlyoutState();
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    v-if="!hasChildren || hasAccessibleChildren"
    ref="groupRef"
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="grid gap-1 text-sm cursor-pointer select-none relative"
  >
    <SidebarGroupHeader
      :icon="icon"
      :name="name"
      :label="label"
      :to="to"
      :getter-keys="getterKeys"
      :is-active="isActive"
      :has-active-child="hasActiveChild"
      :expandable="hasChildren"
      :is-expanded="isExpanded"
      :is-collapsed="isCollapsed"
      @toggle="toggleTrigger"
    />
    <ul
      v-if="hasChildren && !isCollapsed"
      v-show="isExpanded || hasActiveChild"
      class="grid m-0 list-none sidebar-group-children"
    >
      <template v-for="child in children" :key="child.name">
        <SidebarSubGroup
          v-if="child.children"
          :label="child.label"
          :icon="child.icon"
          :children="child.children"
          :is-expanded="isExpanded"
          :active-child="activeChild"
        />
        <SidebarGroupLeaf
          v-else-if="isAllowed(child.to)"
          v-show="isExpanded || activeChild?.name === child.name || pinnedItems.includes(child.name)"
          v-bind="child"
          :active="activeChild?.name === child.name"
        />
      </template>
    </ul>
    <ul v-else-if="isExpandable && isExpanded && !isCollapsed">
      <SidebarGroupEmptyLeaf />
    </ul>

    <!-- Flyout Menu for Collapsed State -->
    <Teleport to="body">
      <div
        v-if="showFlyout"
        ref="flyoutRef"
        class="fixed z-[100] w-48 bg-n-solid-2 border border-n-weak rounded-lg shadow-xl overflow-y-auto py-2"
        :style="flyoutStyle"
      >
        <div
          class="px-3 py-2 text-xs font-semibold text-n-slate-11 uppercase tracking-wider border-b border-n-weak mb-1"
        >
          {{ label }}
        </div>
        <ul class="grid m-0 list-none">
          <template v-for="child in children" :key="child.name">
            <!-- v6.4.62: Subgroups with children render as expandable sections in flyout -->
            <li v-if="child.children && child.children.length > 0" class="list-none">
              <button
                class="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-n-slate-11 hover:bg-n-alpha-2 cursor-pointer border-t border-n-weak mt-1 pt-2"
                @click.stop="flyoutExpandedSubgroups.has(child.name) ? flyoutExpandedSubgroups.delete(child.name) : flyoutExpandedSubgroups.add(child.name)"
              >
                <span v-if="child.icon" :class="child.icon" class="size-4 text-n-slate-10" />
                <span class="flex-1 text-left text-xs font-semibold uppercase tracking-wider text-n-slate-10">
                  {{ child.label }}
                </span>
                <span
                  class="size-3 transition-transform duration-200"
                  :class="flyoutExpandedSubgroups.has(child.name) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                />
              </button>
              <ul
                v-show="flyoutExpandedSubgroups.has(child.name)"
                class="grid m-0 list-none"
              >
                <template
                  v-for="subChild in child.children"
                  :key="subChild.name"
                >
                  <SidebarGroupLeaf
                    v-if="subChild.to && isAllowed(subChild.to)"
                    v-bind="subChild"
                    :active="activeChild?.name === subChild.name"
                  />
                </template>
              </ul>
            </li>
            <!-- Leaf items without children -->
            <SidebarGroupLeaf
              v-else-if="child.to && isAllowed(child.to)"
              v-bind="child"
              :active="activeChild?.name === child.name"
            />
          </template>
        </ul>
      </div>
    </Teleport>
  </Policy>
</template>

<style>
.sidebar-group-children .child-item::before {
  content: '';
  position: absolute;
  width: 0.125rem;
  /* 0.5px */
  height: 100%;
}

.sidebar-group-children .child-item:first-child::before {
  border-radius: 4px 4px 0 0;
}

/* This selects the last child in a group */
/* https://codepen.io/scmmishra/pen/yLmKNLW */
.sidebar-group-children > .child-item:last-child::before,
.sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::before {
  height: 20%;
}

.sidebar-group-children > .child-item:last-child::after,
.sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 12px;
  bottom: calc(50% - 2px);
  border-bottom-width: 0.125rem;
  border-left-width: 0.125rem;
  border-right-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 0 4px;
  left: 0;
}

#app[dir='rtl'] .sidebar-group-children > .child-item:last-child::after,
#app[dir='rtl']
  .sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  right: 0;
  border-bottom-width: 0.125rem;
  border-right-width: 0.125rem;
  border-left-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 4px 0px;
}
</style>
